// Opérations d'image du scanner hors ligne, écrites pour reproduire celles
// du serveur (OpenCV, cf. backend/scan_engine.py) : redimensionnement par
// moyenne de surface (INTER_AREA), redressement en perspective bilinéaire,
// gris pondéré, DCT du pHash. Une image = { data: Uint8Array RGB, width, height }.

export const CARD_W = 400;
export const CARD_H = 560;

/** Image (HTMLImageElement, ImageBitmap, canvas) -> RGB, plus grand côté ≤ maxDim. */
export function imageToRGB(img, maxDim = 1600) {
  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  const k = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.round(w0 * k), h = Math.round(h0 * k);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  const rgba = ctx.getImageData(0, 0, w, h).data;
  const data = new Uint8Array(w * h * 3);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
    data[j] = rgba[i]; data[j + 1] = rgba[i + 1]; data[j + 2] = rgba[i + 2];
  }
  return { data, width: w, height: h };
}

// Poids de la moyenne de surface sur un axe : pour chaque pixel de sortie,
// la liste [indice source, part] de ce qu'il recouvre.
function areaTable(src, dst) {
  const scale = src / dst;
  const tab = [];
  for (let i = 0; i < dst; i++) {
    const a = i * scale, b = (i + 1) * scale;
    const parts = [];
    for (let s = Math.floor(a); s < Math.min(src, Math.ceil(b)); s++) {
      const w = Math.min(b, s + 1) - Math.max(a, s);
      if (w > 1e-9) parts.push(s, w / scale);
    }
    tab.push(parts);
  }
  return tab;
}

/** Recadre (x0,y0,w,h) puis redimensionne par moyenne de surface. */
export function resizeArea(img, dw, dh, crop) {
  const [cx, cy, cw, ch] = crop || [0, 0, img.width, img.height];
  const tx = areaTable(cw, dw), ty = areaTable(ch, dh);
  const tmp = new Float32Array(ch * dw * 3); // passe horizontale
  for (let y = 0; y < ch; y++) {
    const row = ((cy + y) * img.width + cx) * 3;
    for (let x = 0; x < dw; x++) {
      const p = tx[x]; let r = 0, g = 0, b = 0;
      for (let k = 0; k < p.length; k += 2) {
        const o = row + p[k] * 3, w = p[k + 1];
        r += img.data[o] * w; g += img.data[o + 1] * w; b += img.data[o + 2] * w;
      }
      const t = (y * dw + x) * 3; tmp[t] = r; tmp[t + 1] = g; tmp[t + 2] = b;
    }
  }
  const out = new Uint8Array(dw * dh * 3);
  for (let y = 0; y < dh; y++) {
    const p = ty[y];
    for (let x = 0; x < dw; x++) {
      let r = 0, g = 0, b = 0;
      for (let k = 0; k < p.length; k += 2) {
        const t = (p[k] * dw + x) * 3, w = p[k + 1];
        r += tmp[t] * w; g += tmp[t + 1] * w; b += tmp[t + 2] * w;
      }
      const o = (y * dw + x) * 3;
      out[o] = Math.round(r); out[o + 1] = Math.round(g); out[o + 2] = Math.round(b);
    }
  }
  return { data: out, width: dw, height: dh };
}

/** Sous-image [x0, y0, x1, y1] (bornes entières). */
export function cropRGB(img, [x0, y0, x1, y1]) {
  const w = x1 - x0, h = y1 - y0, data = new Uint8Array(w * h * 3);
  for (let y = 0; y < h; y++) {
    const o = ((y0 + y) * img.width + x0) * 3;
    data.set(img.data.subarray(o, o + w * 3), y * w * 3);
  }
  return { data, width: w, height: h };
}

/** Carré s x s, image centrée, gris 114 autour (comme à l'entraînement). */
export function letterbox(img, s) {
  const k = s / Math.max(img.width, img.height);
  const nw = Math.round(img.width * k), nh = Math.round(img.height * k);
  const r = resizeArea(img, nw, nh);
  const out = new Uint8Array(s * s * 3).fill(114);
  const ox = Math.floor((s - nw) / 2), oy = Math.floor((s - nh) / 2);
  for (let y = 0; y < nh; y++) out.set(r.data.subarray(y * nw * 3, (y + 1) * nw * 3), ((y + oy) * s + ox) * 3);
  return { img: { data: out, width: s, height: s }, k, ox, oy };
}

// Homographie qui envoie src[i] sur dst[i] (4 points), système 8x8.
function homography(src, dst) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i], [u, v] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  for (let c = 0; c < 8; c++) { // Gauss avec pivot partiel
    let p = c;
    for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    [A[c], A[p]] = [A[p], A[c]]; [b[c], b[p]] = [b[p], b[c]];
    for (let r = 0; r < 8; r++) {
      if (r === c) continue;
      const f = A[r][c] / A[c][c];
      for (let k = c; k < 8; k++) A[r][k] -= f * A[c][k];
      b[r] -= f * b[c];
    }
  }
  const h = b.map((v, i) => v / A[i][i]);
  return [...h, 1];
}

/** Redresse le quadrilatère (coins HG, HD, BD, BG) en carte 400x560. */
export function warpCard(img, quad, W = CARD_W, H = CARD_H) {
  const m = homography([[0, 0], [W, 0], [W, H], [0, H]], quad); // sortie -> photo
  const out = new Uint8Array(W * H * 3);
  const { data, width, height } = img;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const d = m[6] * x + m[7] * y + m[8];
      const sx = (m[0] * x + m[1] * y + m[2]) / d, sy = (m[3] * x + m[4] * y + m[5]) / d;
      const x0 = Math.floor(sx), y0 = Math.floor(sy), fx = sx - x0, fy = sy - y0;
      const o = (y * W + x) * 3;
      if (x0 < 0 || y0 < 0 || x0 + 1 >= width || y0 + 1 >= height) continue;
      const a = (y0 * width + x0) * 3, b = a + 3, c = a + width * 3, e = c + 3;
      for (let k = 0; k < 3; k++) {
        const top = data[a + k] + (data[b + k] - data[a + k]) * fx;
        const bot = data[c + k] + (data[e + k] - data[c + k]) * fx;
        out[o + k] = Math.round(top + (bot - top) * fy);
      }
    }
  }
  return { data: out, width: W, height: H };
}

/** Rotation de 180° (carte photographiée à l'envers). */
export function rotate180(img) {
  const n = img.width * img.height, out = new Uint8Array(n * 3);
  for (let i = 0; i < n; i++) {
    const j = (n - 1 - i) * 3;
    out[i * 3] = img.data[j]; out[i * 3 + 1] = img.data[j + 1]; out[i * 3 + 2] = img.data[j + 2];
  }
  return { data: out, width: img.width, height: img.height };
}

// ---- pHash : DCT 32x32 -> 8x8, bits au-dessus de la médiane ----
const N = 32;
const COS = (() => {
  const t = new Float64Array(8 * N);
  for (let k = 0; k < 8; k++) {
    const c = k === 0 ? Math.sqrt(1 / N) : Math.sqrt(2 / N);
    for (let n = 0; n < N; n++) t[k * N + n] = c * Math.cos((Math.PI * (2 * n + 1) * k) / (2 * N));
  }
  return t;
})();

function phash64(img, crop) {
  const small = resizeArea(img, N, N, crop);
  const g = new Float64Array(N * N);
  for (let i = 0; i < N * N; i++) {
    const o = i * 3;
    g[i] = (small.data[o] * 4899 + small.data[o + 1] * 9617 + small.data[o + 2] * 1868 + 8192) >> 14;
  }
  const rows = new Float64Array(N * 8); // DCT des lignes (8 premiers coefficients)
  for (let y = 0; y < N; y++) for (let k = 0; k < 8; k++) {
    let s = 0; for (let n = 0; n < N; n++) s += g[y * N + n] * COS[k * N + n];
    rows[y * 8 + k] = s;
  }
  const d = new Float64Array(64);
  for (let ky = 0; ky < 8; ky++) for (let kx = 0; kx < 8; kx++) {
    let s = 0; for (let n = 0; n < N; n++) s += rows[n * 8 + kx] * COS[ky * N + n];
    d[ky * 8 + kx] = s;
  }
  const med = Array.from(d.subarray(1)).sort((a, b) => a - b)[31];
  return Array.from(d, (v) => (v > med ? 1 : 0));
}

/** 128 bits (carte entière + illustration) rangés en 16 octets, bit fort d'abord. */
export function phash128(card) {
  const bits = [...phash64(card), ...phash64(card, [30, 60, 340, 240])];
  const out = new Uint8Array(16);
  for (let i = 0; i < 128; i++) if (bits[i]) out[i >> 3] |= 0x80 >> (i & 7);
  return out;
}

/** Tenseur NCHW 224x224 normalisé pour DINOv2 / CLIP. */
export function toTensorData(card, mean, std) {
  const r = resizeArea(card, 224, 224);
  const n = 224 * 224, out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) for (let c = 0; c < 3; c++) out[c * n + i] = (r.data[i * 3 + c] / 255 - mean[c]) / std[c];
  return out;
}
